import { NgModule, inject, provideAppInitializer } from '@angular/core';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AppService } from './services/app/app.service';
import { BrowserModule } from '@angular/platform-browser';
import { DashboardModule } from './main/dashboard/dashboard.module';
import { EntNavigationService } from './services/navigation/navigation.service';
import { Error403Module } from './main/errors/403/error-403.module';
import { Error404Module } from './main/errors/404/error-404.module';
import { ErrorBrowserModule } from './main/errors/browser/error-browser.module';
import { GraphService } from './services/graph/graph.service';
import { KeycloakService } from './services/keycloak/keycloak.service';
import { LogService } from './services/logs/log.service';
import { LoginModule } from './main/login/login.module';
import { MaterialModule } from './material/material.module';
import { OrgsModule } from './main/orgs/orgs.module';
import { OrgsService } from './services/orgs/orgs.service';
import { PasswordChangeModule } from './main/password/change/password-change.module';
import { PasswordRecoveryModule } from './main/password/recovery/password-recovery.module';
import { PasswordResetModule } from './main/password/reset/password-reset.module';
import { PortailModule } from './main/portail/portail.module';
import { ResourceDetailModule } from './main/exchange/ressources/resource-detail/resource-detail.module';
import { ResourcesModule } from './main/exchange/ressources/resources.module';
import { RolesModule } from './main/roles/roles.module';
import { SelfModule } from './main/self/self.module';
import { SettingsModule } from './main/settings/settings.module';
import { Services } from './services/services/services.service';
import { ErrorMaintenanceModule } from './main/errors/maintenance/error-maintenance.module';
import { SharemailboxDetailModule } from './main/exchange/sharemailbox/sharemailbox-detail/sharemailbox-detail.module';
import { SharemailboxModule } from './main/exchange/sharemailbox/sharemailbox.module';
import { StartModule } from './main/start/start.module';
import { SupportModule } from './main/support/support.module';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { UserCreateModule } from './main/user-management/create/user-create.module';
import { UserModule } from './main/user-management/user/user.module';
import { UsersModule } from './main/user-management/users/users.module';
import { UsersService } from './services/users/users.service';
import { ValidationModule } from './main/validation/validation.module';
import { ImageUploadModule } from './main/image-upload-dialog/image-upload-dialog.module';
import { ImageDeleteModule } from './main/image-upload-dialog/image-delete-dialog/image-delete-dialog.module';
import { AssistanceModule } from './main/assistance/assistance.module';
import { ServiceNowService } from './services/servicenow/servicenow.service';
import { UserMicrosoftProfilModule } from './main/m365/user-microsoft-profil/user-microsoft-profil.module';
import { MaintenancePageModule } from './main/maintenance-page/maintenance-page.module';
import { MaintenanceDialogModule } from './main/maintenance-page/maintenance-dialog/maintenance-dialog.module';
import { provideMatomo } from 'ngx-matomo-client';
import { withRouter } from 'ngx-matomo-client';
import { environment } from 'src/environments/environment';
import { UpiService } from './services/upi/upi.service';
import { NameChangeModule } from './main/user-management/name-change/name-change.module';
import { SettingsService } from './services/settings/settings.service';
import { AdministrationModule } from './main/administration/administration.module';
import { TeamsModule } from './main/exchange/teams/teams.module';
import { TeamDetailModule } from './main/exchange/teams/team-detail/team-detail.module';
import { GroupInfoModule } from './main/m365/groups/group-info/group-info.module';
import { ToolBoxService } from './services/toolbox/toolbox.service';
import { ErrorSnackbarComponent } from './main/snackbars/error-snackbar/error-snackbar.component';
import { SuccesSnackbarComponent } from './main/snackbars/succes-snackbar/success-snackbar.component';
import { LoadingSnackbarComponent } from './main/snackbars/loading-snackbar/loading-snackbar.component';
import { provideNativeDateAdapter } from '@angular/material/core';
import { LoggedLayoutComponent } from './layout/logged-layout/logged-layout.component';
import { LogoutLayoutComponent } from './layout/logout-layout/logout-layout.component';
import { DeletedTeamsModule } from './main/m365/deleted-teams/deleted-teams.module';
import { logInterceptor } from './interceptors/log.interceptor';
import { tokenInterceptor } from './interceptors/token.interceptor';
import { errorInterceptor } from './interceptors/error.interceptor';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    MaterialModule,
    BrowserModule,
    GroupInfoModule,
    TeamsModule,
    DeletedTeamsModule,
    TeamDetailModule,
    NameChangeModule,
    PasswordRecoveryModule,
    ResourcesModule,
    ResourceDetailModule,
    SharemailboxModule,
    SharemailboxDetailModule,
    UserMicrosoftProfilModule,
    PasswordChangeModule,
    PasswordResetModule,
    SupportModule,
    LoginModule,
    StartModule,
    PortailModule,
    AdministrationModule,
    AssistanceModule,
    RolesModule,
    OrgsModule,
    DashboardModule,
    UserCreateModule,
    UserModule,
    MaintenancePageModule,
    UsersModule,
    SelfModule,
    SettingsModule,
    ImageUploadModule,
    ImageDeleteModule,
    MaintenanceDialogModule,
    ValidationModule,
    ErrorBrowserModule,
    ErrorMaintenanceModule,
    Error403Module,
    Error404Module,
    AppRoutingModule,
    ErrorSnackbarComponent,
    SuccesSnackbarComponent,
    LoadingSnackbarComponent,
    LoggedLayoutComponent,
    LogoutLayoutComponent,
  ],
  providers: [
    provideAppInitializer(async () => {
      const keycloak = inject(KeycloakService);
      try {
        return await keycloak.init({
          onLoad: 'check-sso',
          checkLoginIframe: false,
        });
      } catch (error) {
        return console.error('Keycloak initialization failed:', error);
      }
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    UsersService,
    AppService,
    LogService,
    GraphService,
    ToolBoxService,
    OrgsService,
    EntNavigationService,
    Services,
    ServiceNowService,
    SettingsService,
    provideMatomo(
      {
        trackerUrl: environment.matomoConfig.url,
        siteId: environment.matomoConfig.siteId,
        disabled: environment.matomoConfig.disabled,
      },
      withRouter({ exclude: ['/password-reset', '/user/'] })
    ),
    ServiceNowService,
    UpiService,
    provideHttpClient(
      withInterceptors([logInterceptor, tokenInterceptor, errorInterceptor])
    ),
    provideNativeDateAdapter(),
  ],
})
export class AppModule {}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
